import {Injectable, Injector} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {NGXLogger} from 'ngx-logger';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FakturierungsbelegEntitiesActions} from '../actions/fakturierungsbeleg-entities.actions';
import {concatMap, exhaustMap, of, switchMap, take, tap, withLatestFrom} from 'rxjs';
import {catchError, filter, map} from 'rxjs/operators';
import {Action, ActionCreator, Store} from '@ngrx/store';
import {AppState} from '../states/app.state';
import {
  BelegDTO,
  BelegService,
  CreateBelegRequestDTO,
  EmailversandService,
  KonvertiereBelegRequestDTO,
  PageableDTO,
  ProblemDTO,
  StorniereBelegResponseDTO,
  ValidationService,
} from '../../openapi/fakturierung-openapi';
import {
  InhaberEntitiesSelectors,
  MappedHttpErrorResponse,
  mappedHttpErrorResponseOperator,
  RouterActions
} from '@adnova/jf-ng-components';
import {TypedAction} from '@ngrx/store/src/models';
import {FakturierungsbelegEntitiesSelectors} from '../selectors/fakturierungsbeleg-entities.selectors';
import {FakturierungsbelegFormActions} from '../actions/fakturierungsbeleg-form.actions';
import {SentryActions} from '../actions/sentry.actions';
import {FakturierungsbelegContentActions} from '../actions/fakturierungsbeleg-content.actions';
import {FakturierungsbelegTableActions} from '../actions/fakturierungsbeleg-table.actions';
import {SendenDialogActions} from '../actions/senden-dialog.actions';
import {KundeEntitiesSelectors} from '../selectors/kunde-entities.selectors';
import {EinstellungenEntitiesSelectors} from '../selectors/einstellungen-entities.selectors';
import {IntentActionService} from '../../services/intent-action.service';
import {FakturierungsbelegFormSelectors} from '../selectors/fakturierungsbeleg-form.selectors';
import {Belegart} from '../../util/belegart.enum';
import {getVersandstatusTyp} from '../../util/versandstatus.util';
import {createUpdateBelegRequest} from '../../util/beleg-request';


@Injectable()
export class FakturierungsbelegEntitiesEffects {

  private _intentActionService?: IntentActionService;

  get intentActionService(): IntentActionService {
    if (!this._intentActionService) {
      this._intentActionService = this.injector.get(IntentActionService);
    }

    return this._intentActionService;
  }

  constructor(
    private actions$: Actions,
    private logger: NGXLogger,
    private snackbar: MatSnackBar,
    private belegService: BelegService,
    private validationService: ValidationService,
    private emailversandService: EmailversandService,
    private store: Store<AppState>,
    private injector: Injector,
  ) {
  }

  /**
   * Der Effekt erlaubt asynchrones Laden von Fakturierungsbelegen durch serverseitige Datenanfragen.
   * Dabei steuert er die Auslösung von Actions basierend auf dem Erfolg oder Misserfolg der Anfrage.
   *
   * @property onAction - Action Creator, der ausgeführt wird, um den Effekt zu starten
   * @property successAction - Action Creator, der ausgeführt wird, wenn die Daten erfolgreich vom Server abgerufen wurden
   * @property failureAction - Action Creator, der ausgeführt wird, wenn beim Abrufen der Daten ein Fehler aufgetreten ist
   */
  readonly readFakturierungsbelege$ = (
    onAction: ActionCreator<any, (props: {
      betriebId: string,
      pageableDto: PageableDTO,
    }) => ({
      betriebId: string, pageableDto: PageableDTO,
    } & TypedAction<any>)>,
    successAction?: ActionCreator<any, (props: { belegDtos: BelegDTO[], }) => ({
      belegDtos: BelegDTO[]
    } & TypedAction<any>)>,
    failureAction?: ActionCreator<any, (props: { error: MappedHttpErrorResponse, }) => ({
      error: MappedHttpErrorResponse
    } & TypedAction<any>)>,
  ) => createEffect(
    () => this.actions$.pipe(
      ofType(onAction),
      switchMap(({betriebId, pageableDto}) => {
        return this.belegService.readBelege(
          betriebId,
          pageableDto
        ).pipe(
          switchMap(belegPageDto => {
            this.logger.debug('read fakturierungsbelege succeeded.');

            return [
              FakturierungsbelegEntitiesActions.readFakturierungsbelegeSuccess({belegDtos: belegPageDto.content}),
              FakturierungsbelegEntitiesActions.countFakturierungsbelegeElementsSuccess({totalElements: belegPageDto.totalElements}),
              FakturierungsbelegEntitiesActions.countFakturierungsbelegePagesSuccess({totalPages: Math.ceil(belegPageDto.totalElements / (belegPageDto.pageable.size || 1))}),
              ...(successAction ? [successAction({belegDtos: belegPageDto.content})] : []),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => {
              this.logger.error('read fakturierungsbelege failed.', 'error:', error);

              return [
                FakturierungsbelegEntitiesActions.readFakturierungsbelegeFailure({error}),
                ...(failureAction ? [failureAction({error})] : []),
              ];
            }),
          )),
        );
      }),
    )
  );

  /**
   * Generisches Error-Handling für das Laden von Fakturierungsbelegen.
   */
  readonly readFakturierungsbelegeFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.readFakturierungsbelegeFailure),
      map(({error}) => {

        let errorMsg = '';
        switch (error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Laden von Fakturierungsbelegen. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Keine Fakturierungsbelege gefunden.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Laden der Fakturierungsbelege. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          });
      }),
    ), {dispatch: false}
  );

  /**
   * Effekt zum Laden eines Fakturierungsbelegs anhand der ID, sofern er noch nicht im Store vorhanden ist.
   */
  readonly loadFakturierungsbelegByIdIfAbsent$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.loadFakturierungsbelegByIdIfAbsent),
      concatMap(action => this.store.select(FakturierungsbelegEntitiesSelectors.belegById(action.belegId)).pipe(
        take(1),
        map(beleg => ({action, beleg})),
      )),
      filter(data => !data.beleg),
      map(data => FakturierungsbelegEntitiesActions.getFakturierungsbelegById({
        betriebId: data.action.betriebId,
        belegId: data.action.belegId,
      })),
    ),
  );

  /**
   * Effekt zum Laden eines Fakturierungsbelegs anhand der ID.
   */
  readonly getFakturierungsbelegById$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.getFakturierungsbelegById),
      concatMap(({betriebId, belegId}) => {
        return this.belegService.getBeleg(betriebId, belegId).pipe(
          switchMap((belegDto) => {
            // INFO: Bewusst KEIN Log des kompletten DTO — Beleg enthaelt PII
            // (Kundendaten, Empfaengeradressen, Fehlermeldungen). Effect wird
            // bei jedem Versandstatus-Polling-Tick getriggert; ungefilterte
            // Logs landen in Konsole und Sentry-Breadcrumbs (CWE-532).
            const versandstatusTyp = getVersandstatusTyp(belegDto.aktuellerVersandstatus?.status);
            this.logger.debug(
              'read belegDto by ID succeeded. belegId={}, versandstatusTyp={}',
              belegDto.id,
              versandstatusTyp ?? '-',
            );
            const followUp: Action[] = [
              FakturierungsbelegEntitiesActions.getFakturierungsbelegByIdSuccess({belegDto}),
            ];
            // INFO: Validation ist nur fuer Belege im Status "offen" (Entwurf)
            // sinnvoll — festgeschriebene oder stornierte Belege koennen nicht
            // mehr veraendert werden, ein Validation-Roundtrip ist redundant.
            // Damit entfallen u.a. die unnoetigen Validation-Calls beim
            // Versandstatus-Polling auf festgeschriebenen Belegen.
            if (belegDto.status?.offen) {
              followUp.push(FakturierungsbelegEntitiesActions.validateFakturierungsbeleg({
                betriebId: belegDto.betriebId,
                belegId: belegDto.id,
              }));
            }
            return followUp;
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error(
                'read beleg by ID failed. belegId:',
                belegId,
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.getFakturierungsbelegByIdFailure({
                error,
              });
            }),
          )),
        );
      }),
    ),
  );

  /**
   * Error-Handling für das Laden eines Fakturierungsbelegs anhand der ID.
   */
  readonly getFakturierungsbelegByIdFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.getFakturierungsbelegByIdFailure),
      map(({error}) => {

        let errorMsg = '';
        switch (error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Laden des Fakturierungsbelegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Fakturierungsbeleg nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Laden des Fakturierungsbelegs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );
      })
    ),
    {dispatch: false},
  );

  /**
   * Effekt zum Erstellen eines Fakturierungsbelegs mit dem aktuell ausgewählten Betrieb.
   *
   * Dieser Effekt reagiert auf die Action, die den Fakturierungsbeleg erstellen soll.
   * Er führt folgende Schritte aus:
   * 1. Sobald die Action ausgelöst wird, wird die aktuell ausgewählte Betriebs-ID aus dem Store abgerufen.
   * 2. Es wird überprüft, ob eine gültige Betriebs-ID vorhanden ist.
   * 3. Mit der Betriebs-ID wird der Selector 'zahlungszielTageByBetrieb' verwendet, um den Wert für 'zahlungszielTage'
   *    (Zahlungsziel in Tagen) aus den Einstellungen zu holen.
   * 4. Mit den gesammelten Informationen wird ein DTO (Data Transfer Object) erstellt.
   * 5. Schließlich wird eine neue Action dispatcht, die den Fakturierungsbeleg mit den erstellten Daten anlegt.
   */
  readonly createFakturierungsbelegWithSelectedBetrieb$ = createEffect(() =>
    this.actions$.pipe(
      // INFO: 1. Schritt: Reagiere auf die Action 'createFakturierungsbelegWithCurrentBetriebId'
      ofType(FakturierungsbelegEntitiesActions.createFakturierungsbelegWithCurrentBetriebId),

      // INFO: 2. Kombiniere die Action mit der aktuell ausgewählten Betriebs-ID aus dem Store
      withLatestFrom(this.store.select(InhaberEntitiesSelectors.currentInhaberId)),

      // INFO: 3. Stelle sicher, dass eine gültige Betriebs-ID vorhanden ist (nicht null oder leer)
      filter(([, betriebId]) => !!betriebId),

      // INFO: 4. Verwende switchMap, um auf Basis der Betriebs-ID den Wert 'zahlungszielTage' abzurufen
      switchMap(([{redirectToForm, belegart}, betriebId]) =>
        this.store
          .select(EinstellungenEntitiesSelectors.zahlungszielTageByBetrieb(betriebId!))
          .pipe(
            // INFO: Nehme nur den ersten Wert, da wir nur einmal den aktuellen Wert benötigen
            take(1),
            // INFO: Erstelle ein Objekt mit allen benötigten Werten, um später den Beleg zu erstellen
            map(zahlungszielTage => ({
              redirectToForm,
              betriebId: betriebId!,
              zahlungszielTage,
              belegart,
            }))
          )
      ),

      // INFO: 5. Erstelle das DTO (Data Transfer Object) und dispatch die Action zum Erstellen des Fakturierungsbelegs
      map(({redirectToForm, betriebId, zahlungszielTage, belegart}) => {
        // INFO: Erstelle ein Objekt, das alle nötigen Daten für den Fakturierungsbeleg enthält
        const createBelegRequestDto: CreateBelegRequestDTO = {
          belegart: belegart,
          // INFO: Aktuelles Datum im Format YYYY-MM-DD
          datum: new Date().toISOString().split('T')[0],
          leistungsdatum: new Date().toISOString().split('T')[0],
          // INFO: Keine Vorlaufzeile mitgeben → der Service setzt den sprachspezifischen Default
          // (leer/null = "Default gewollt"). Eigener Text wird erst beim Editieren gesendet.
          // INFO: Zahlungsziel in Tagen (kann eine Zahl oder undefined sein)
          zahlungszielTage,
        };

        // INFO: Dispatch die Action, die den Fakturierungsbeleg erstellt, mit allen erforderlichen Informationen
        return FakturierungsbelegEntitiesActions.createFakturierungsbeleg({
          betriebId,
          createBelegRequestDto,
          redirectToForm,
        });
      })
    )
  );

  /**
   * Plant die Emission des "Festgeschrieben"-Intents nach 10 Sekunden. Der Delay
   * gibt dem Beleg-Service Zeit, den festgeschriebenen Beleg asynchron zu
   * uebertragen, bevor die Belege-Online-App ueber den Intent zum Nachladen
   * angestoßen wird.
   */
  private scheduleFestgeschriebenIntent(betriebId: string, belegId: string): void {
    setTimeout(() => {
      if (this.intentActionService.isRunningInPortal()) {
        this.intentActionService.doFakturierungsbelegFestgeschriebenV1({
          fakturierungsbelegId: belegId,
          betriebId,
        });
      } else {
        this.logger.debug('not running in portal, festgeschrieben broadcast skipped.');
      }
    }, 10000);
  }

  /**
   * Effekt zum Erstellen eines Fakturierungsbelegs.
   */
  readonly createFakturierungsbeleg$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.createFakturierungsbeleg),
      concatMap(({betriebId, createBelegRequestDto, redirectToForm}) => {
        return this.belegService.createBeleg(betriebId, createBelegRequestDto).pipe(
          concatMap(createdBelegDto => {
            this.logger.debug(
              'create fakturierungsbeleg entity succeeded. belegId:',
              createdBelegDto.id,
            );

            const successActions: TypedAction<any>[] = [
              FakturierungsbelegEntitiesActions.createFakturierungsbelegSuccess({belegDto: createdBelegDto}),
              FakturierungsbelegEntitiesActions.validateFakturierungsbeleg({
                betriebId: createdBelegDto.betriebId,
                belegId: createdBelegDto.id,
              }),
            ];

            if (redirectToForm) {
              successActions.push(FakturierungsbelegFormActions.redirectToForm({
                betriebId,
                belegId: createdBelegDto.id
              }));
            }

            return successActions;
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error(
                'create fakturierungsbeleg entity failed',
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.createFakturierungsbelegFailure({
                error,
              });
            }),
          )),
        );
      }),
    )
  );

  /**
   * Success-Handling für das Erstellen eines Fakturierungsbelegs.
   */
  readonly createFakturierungsbelegSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.createFakturierungsbelegSuccess),
      tap(() => {
        this.snackbar.open('Beleg erfolgreich angelegt', undefined, {duration: 5000});
      }),
    ), {dispatch: false}
  );

  /**
   * Error-Handling für das Erstellen eines Fakturierungsbelegs.
   */
  readonly createFakturierungsbelegFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.createFakturierungsbelegFailure),
      map(action => {
        let errorMsg = '';
        switch (action.error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Erstellen des Fakturierungsbelegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Betrieb nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Erstellen des Fakturierungsbelegs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  /**
   * Effekt zum Updaten eines Fakturierungsbelegs.
   */
  readonly updateFakturierungsbeleg$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.updateFakturierungsbeleg),
      concatMap(({betriebId, belegId, updateBelegRequestDto, fetchPositions: fetchPositionen}) => {
        return this.belegService.updateBeleg(betriebId, belegId, updateBelegRequestDto).pipe(
          switchMap(updatedBelegDto => {
            this.logger.debug(
              'update beleg entity succeeded. belegId:',
              belegId,
            );

            const actions: TypedAction<any>[] = [
              FakturierungsbelegEntitiesActions.updateFakturierungsbelegSuccess({
                belegDto: updatedBelegDto,
              }),
            ];

            if (fetchPositionen) {
              actions.push(FakturierungsbelegFormActions.fetchPositionen({belegId}));
            }

            return actions;
          }),

          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error(
                'update beleg entity failed. belegId:',
                belegId,
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.updateFakturierungsbelegFailure({
                error,
              });
            }),
          )),
        );
      }),
    )
  );

  /**
   * Success-Handling für das Updaten eines Fakturierungsbelegs.
   */
  readonly updateFakturierungsbelegSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.updateFakturierungsbelegSuccess),
      map(({belegDto}) => {
        return FakturierungsbelegEntitiesActions.validateFakturierungsbeleg({
          betriebId: belegDto.betriebId,
          belegId: belegDto.id,
        });
      }),
    )
  );

  /**
   * Error-Handling für das Updaten eines Fakturierungsbelegs.
   */
  readonly updateFakturierungsbelegFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.updateFakturierungsbelegFailure),
      map(action => {
        let errorMsg = '';
        switch (action.error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Aktualisieren des Fakturierungsbelegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Betrieb nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Aktualisieren des Fakturierungsbelegs. Bitte probiere es später erneut.';
          }
        }
        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );
        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  /**
   * Effekt zum Löschen eines Fakturierungsbelegs.
   */
  readonly deleteFakturierungsbeleg$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.deleteFakturierungsbeleg),
      concatMap(({betriebId, belegId}) => {
        return this.belegService.deleteBeleg(betriebId, belegId).pipe(
          switchMap(() => {
            this.logger.debug(
              'delete beleg succeeded. belegId:',
              belegId,
            );
            return [
              FakturierungsbelegEntitiesActions.deleteFakturierungsbelegSuccess({belegId}),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error(
                'delete beleg failed.',
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.deleteFakturierungsbelegFailure({
                error,
              });
            }),
          )),
        );
      }),
    )
  );

  /**
   * Success-Handling für das Löschen eines Fakturierungsbelegs.
   */
  readonly deleteFakturierungsbelegSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.deleteFakturierungsbelegSuccess),
      tap(() => {
        this.snackbar.open('Beleg erfolgreich gelöscht', undefined, {duration: 5000});
      }),
    ), {dispatch: false}
  );

  /**
   * Error-Handling für das Löschen eines Fakturierungsbelegs.
   */
  readonly deleteFakturierungsbelegFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.deleteFakturierungsbelegFailure),
      map(({error}) => {

        let errorMsg = '';
        switch (error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Löschen des Fakturierungsbelegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Fakturierungsbeleg nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Löschen des Fakturierungsbelegs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  /**
   * Effekt zum Festschreiben eines Fakturierungsbelegs.
   */
  readonly finalizeFakturierungsbeleg$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.finalizeFakturierungsbeleg),
      concatMap(({betriebId, belegId, openPreview, downloadPdf, openSendenDialog}) => {
        return this.belegService.schreibeBeleg(betriebId, belegId).pipe(
          /*
           * INFO:
           * Das Übertragen des festgeschriebenen Belegs zum Beleg-Service ist ein asynchroner Prozess.
           * Daher wird hier ein Timeout von 10 Sekunden gesetzt, bevor der Intent an die Belege-Online
           * App gesendet wird. Der Intent wird in der Belege-Online App verwendet, um die Belege
           * nachzuladen.
           *
           * Der Intent wird genau einmal nach dem erfolgreichen schreibeBeleg-Aufruf geplant —
           * der folgende buildActions()-Helper ist damit eine reine Action-Factory ohne Side-Effects.
           */
          tap(() => {
            this.scheduleFestgeschriebenIntent(betriebId, belegId);
          }),
          concatMap(belegDto => {
            this.logger.debug(
              'finalize beleg succeeded. belegId:',
              belegId,
            );

            const buildActions = (empfaengerEmail?: string): TypedAction<any>[] => {
              const actions: TypedAction<any>[] = [
                FakturierungsbelegEntitiesActions.finalizeFakturierungsbelegSuccess({belegDto}),
              ];

              if (openPreview) {
                actions.push(RouterActions.navigateByUrl({
                  url: `/fakturierungsbelege/inhaber/` + betriebId + `/preview/fakturierungsbeleg/` + belegId
                }));

              } else {
                actions.push(RouterActions.navigateByUrl({
                  url: `/fakturierungsbelege/inhaber/` + betriebId
                }));
              }

              if (downloadPdf) {
                actions.push(FakturierungsbelegContentActions.downloadBelegPdf({
                  betriebId,
                  belegId,
                  statusEntwurf: false,
                  isPreviewMode: false,
                  belegnummer: belegDto.nummer || '',
                }));
              }

              if (openSendenDialog) {
                actions.push(SendenDialogActions.open({
                  betriebId,
                  belegId,
                  belegnummer: belegDto.nummer || '',
                  belegart: belegDto.belegart as Belegart,
                  empfaengerEmail,
                }));
              }

              return actions;
            };

            if (openSendenDialog) {
              return this.store.select(KundeEntitiesSelectors.kundenById(belegDto.kundeId || '')).pipe(
                take(1),
                concatMap(kunde => buildActions(kunde?.kundendaten.emailAdresse)),
              );
            }

            return buildActions();
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map(error => {
              this.logger.error(
                'finalize beleg failed.',
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.finalizeFakturierungsbelegFailure({
                error,
              });
            }),
          )),
        );
      }),
    )
  );

  /**
   * Error-Handling für das Festschreiben eines Fakturierungsbelegs.
   */
  readonly finalizeFakturierungsbelegFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.finalizeFakturierungsbelegFailure),
      map(({error}) => {

        let errorMsg = '';
        switch (error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Fertigstellen des Belegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Beleg nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          case 422 : {
            errorMsg = error.error?.detail || 'Der Beleg erfüllt nicht die Anforderungen, um fertiggestellt werden zu können. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Fertigstellen des Belegs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          error.error?.detail ?? errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );


  /**
   * Effekt zum Stornieren eines Fakturierungsbelegs.
   */
  readonly cancelFakturierungsbeleg$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.cancelFakturierungsbeleg),
      concatMap(({betriebId, belegId, openPreview, downloadPdf}) => {
        return this.belegService.storniereBeleg(betriebId, belegId).pipe(
          concatMap((response: StorniereBelegResponseDTO) => {
            this.logger.debug('cancel beleg succeeded. belegId:', belegId);
            // INFO: korrekturbeleg ist nur gesetzt, wenn der Storno einen erzeugt hat (Rechnung/Gutschrift).
            const korrekturbeleg = response.korrekturbeleg;
            // INFO: Ziel fuer Vorschau und PDF ist der Korrekturbeleg, sonst der stornierte Beleg selbst.
            const zielBeleg = korrekturbeleg || response.beleg;
            this.snackbar.open(
              korrekturbeleg
                ? 'Hinweis: Bitte übersende deinem Kunden diese Belegkorrektur.'
                : 'Der Beleg wurde storniert.',
              undefined,
              {duration: 20000},
            );

            const actions: TypedAction<any>[] = [
              FakturierungsbelegEntitiesActions.cancelFakturierungsbelegSuccess({
                cancelledBeleg: response.beleg,
                correctedBeleg: korrekturbeleg,
              }),
              // INFO: nur der Korrekturbeleg ist ein neuer Beleg; der stornierte steht schon in der Liste.
              ...(korrekturbeleg
                ? [FakturierungsbelegTableActions.addNewItemToDisplayedIds({belegId: korrekturbeleg.id})]
                : []),
              FakturierungsbelegEntitiesActions.validateFakturierungsbeleg({
                betriebId: zielBeleg.betriebId,
                belegId: zielBeleg.id,
              }),
            ];

            if (openPreview) {
              actions.push(RouterActions.navigateByUrl({
                url: `/fakturierungsbelege/inhaber/` + betriebId + `/preview/fakturierungsbeleg/` + zielBeleg.id
              }));
            } else {
              actions.push(RouterActions.navigateByUrl({
                url: `/fakturierungsbelege/inhaber/` + betriebId
              }));
            }

            if (downloadPdf) {
              actions.push(FakturierungsbelegContentActions.downloadBelegPdf({
                betriebId,
                belegId: zielBeleg.id,
                statusEntwurf: false,
                isPreviewMode: false,
                belegnummer: zielBeleg.nummer || '',
              }));
            }

            /*
             * INFO:
             * Das Übertragen des stornierten Belegs zum Beleg-Service ist ein asynchroner Prozess.
             * Daher wird hier ein Timeout von 10 Sekunden gesetzt,
             * bevor der Intent an die Belege-Online App gesendet wird.
             *
             * Der Intent wird in der Belege-Online App verwendet, um die Belege nachzuladen.
             */
            setTimeout(() => {
              if (this.intentActionService.isRunningInPortal()) {
                this.intentActionService.doFakturierungsbelegStorniertV1({
                  fakturierungsbelegId: belegId,
                  betriebId,
                });
              } else {
                this.logger.debug('not running in portal, storniert broadcast skipped.');
              }
            }, 10000);

            return actions;
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map(error => {
              this.logger.error(
                'cancel beleg failed.',
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.cancelFakturierungsbelegFailure({
                error,
              });
            }),
          )),
        );
      }),
    )
  );


  /**
   * Error-Handling für das Stornieren eines Fakturierungsbelegs.
   */
  readonly cancelFakturierungsbelegFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.cancelFakturierungsbelegFailure),
      map(({error}) => {

        let errorMsg = '';
        switch (error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Stornieren des Belegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Beleg nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          case 422 : {
            errorMsg = error.error?.detail || 'Der Beleg erfüllt nicht die Anforderungen, um storniert werden zu können. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Stornieren des Belegs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  /**
   * Effekt zum Duplizieren eines Fakturierungsbelegs.
   */
  readonly duplicateFakturierungsbeleg$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.duplicateFakturierungsbeleg),
      exhaustMap(({betriebId, belegId}) => {
        return this.belegService.duplizierBeleg(betriebId, belegId).pipe(
          concatMap((belegDto: BelegDTO) => {
            this.logger.debug('duplicate beleg succeeded. belegId:', belegId);
            this.snackbar.open('Beleg erfolgreich dupliziert.', undefined, {duration: 5000});

            return [
              FakturierungsbelegEntitiesActions.duplicateFakturierungsbelegSuccess({belegDto}),
              FakturierungsbelegEntitiesActions.validateFakturierungsbeleg({
                betriebId: belegDto.betriebId,
                belegId: belegDto.id,
              }),
              FakturierungsbelegTableActions.addNewItemToDisplayedIds({belegId: belegDto.id}),
              RouterActions.navigateByUrl({
                url: `/fakturierungsbelege/inhaber/${betriebId}/edit/fakturierungsbeleg/${belegDto.id}`
              }),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map(error => {
              this.logger.error(
                'duplicate beleg failed.',
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.duplicateFakturierungsbelegFailure({
                error,
              });
            }),
          )),
        );
      }),
    )
  );

  readonly createRechnungAusLieferschein$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.createRechnungAusLieferschein),
      exhaustMap(({betriebId, belegId}) => {
        const request: KonvertiereBelegRequestDTO = {zielBelegart: Belegart.Rechnung};
        return this.belegService.konvertiereBeleg(betriebId, belegId, request).pipe(
          switchMap((rechnungDto: BelegDTO) => this.belegService.getBeleg(betriebId, belegId).pipe(
            // INFO: Die Konvertierung war hier bereits erfolgreich — ein Fehler des reinen
            //       Status-Refreshs darf nicht als Konvertierungs-Fehler gemeldet werden.
            //       Der Reducer patcht den Lieferschein-Status in dem Fall lokal.
            catchError(refreshError => {
              this.logger.warn(
                'refresh lieferschein after konvertierung failed. belegId:',
                belegId,
                'error:',
                refreshError,
              );
              return of<BelegDTO | undefined>(undefined);
            }),
            concatMap((lieferscheinDto: BelegDTO | undefined) => {
              this.logger.debug('create rechnung from lieferschein succeeded. belegId:', belegId);
              this.snackbar.open('Beleg erfolgreich erstellt.', undefined, {duration: 5000});

              return [
                FakturierungsbelegEntitiesActions.createRechnungAusLieferscheinSuccess({rechnungDto, lieferscheinDto}),
                FakturierungsbelegEntitiesActions.validateFakturierungsbeleg({
                  betriebId: rechnungDto.betriebId,
                  belegId: rechnungDto.id,
                }),
                FakturierungsbelegTableActions.addNewItemToDisplayedIds({belegId: rechnungDto.id}),
                RouterActions.navigateByUrl({
                  url: `/fakturierungsbelege/inhaber/${betriebId}/edit/fakturierungsbeleg/${rechnungDto.id}`
                }),
              ];
            }),
          )),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map(error => {
              this.logger.error(
                'create rechnung from lieferschein failed.',
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.createRechnungAusLieferscheinFailure({
                error,
              });
            }),
          )),
        );
      }),
    )
  );

  /**
   * Error-Handling für das Erstellen einer Rechnung aus einem Lieferschein.
   */
  readonly createRechnungAusLieferscheinFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.createRechnungAusLieferscheinFailure),
      map(({error}) => {

        let errorMsg = '';
        switch (error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Erstellen des Belegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Beleg nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          case 422 : {
            errorMsg = 'Der Beleg kann derzeit nicht in eine Rechnung überführt werden.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Erstellen des Belegs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error,
          extras: {
            errorMsg,
          },
        });
      }),
    )
  );

  /**
   * Error-Handling für das Duplizieren eines Fakturierungsbelegs.
   */
  readonly duplicateFakturierungsbelegFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.duplicateFakturierungsbelegFailure),
      map(({error}) => {

        let errorMsg = '';
        switch (error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Duplizieren des Fakturierungsbelegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Fakturierungsbeleg nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          case 422 : {
            errorMsg = 'Ein Korrekturbeleg kann nicht dupliziert werden. Nutze stattdessen den ursprünglichen Beleg.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Duplizieren des Fakturierungsbelegs. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  readonly versendeBeleg$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.versendeBeleg),
      concatMap(({betriebId, belegId, empfaenger}) => {
        return this.emailversandService.versendeBeleg(betriebId, belegId, {empfaenger}).pipe(
          concatMap(response => {
            this.logger.debug('versende beleg succeeded. belegId:', belegId);

            this.snackbar.open(
              'E-Mail wird versendet',
              undefined,
              {
                duration: 5000,
              }
            );

            return [
              FakturierungsbelegEntitiesActions.versendeBelegSuccess({response}),
              SendenDialogActions.versendeBelegSucceeded(),
              SendenDialogActions.close(),
              FakturierungsbelegEntitiesActions.getFakturierungsbelegById({betriebId, belegId}),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            switchMap(error => {
              this.logger.error(
                'versende beleg failed.',
                'error:',
                error,
              );

              return [
                FakturierungsbelegEntitiesActions.versendeBelegFailure({
                  error,
                }),
                SendenDialogActions.versendeBelegFailed(),
              ];
            }),
          )),
        );
      }),
    )
  );

  readonly versendeBelegFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.versendeBelegFailure),
      map(({error}) => {

        let errorMsg = '';
        switch (error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für den Versand des Belegs. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Beleg nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          case 409 : {
            errorMsg = 'Für diesen Beleg läuft bereits ein Versandauftrag. Bitte warte auf dessen Abschluss.';
            break;
          }
          default: {
            errorMsg = 'Die E-Mail konnte nicht versendet werden. Bitte versuche es erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  /**
   * Effekt zum Validieren eines Fakturierungsbelegs
   */
  readonly validateFakturierungsbeleg$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.validateFakturierungsbeleg),
      concatMap(({betriebId, belegId}) => {
        return this.validationService.validate(betriebId, belegId).pipe(
          switchMap(() => {
            this.logger.debug('validate beleg succeeded. belegId:', belegId);

            return [
              FakturierungsbelegEntitiesActions.validateFakturierungsbelegSuccess({belegId}),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map(error => {
              this.logger.error(
                'validate beleg failed.',
                'error:',
                error,
              );

              return FakturierungsbelegEntitiesActions.validateFakturierungsbelegFailure({
                belegId,
                error,
              });
            }),
          )),
        );
      }),
    )
  );

  readonly updateFakturierungsbelegEinstellungen$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.updateFakturierungsbelegEinstellungen),
      withLatestFrom(
        this.store.select(FakturierungsbelegFormSelectors.fakturierungsbelegDto),
      ),
      concatMap(([{betriebId, belegId, useLatestEinstellungen}, fakturierungsbelegDto]) => {
        // Load the default zahlungszielTage from einstellungen
        return this.store.select(EinstellungenEntitiesSelectors.zahlungszielTageByBetrieb(betriebId)).pipe(
          take(1),
          switchMap(zahlungszielTageFromEinstellungen => {
            const belegDto = createUpdateBelegRequest(
              fakturierungsbelegDto,
              useLatestEinstellungen ? {zahlungszielTage: zahlungszielTageFromEinstellungen} : {},
            );

            return this.belegService.updateBeleg(betriebId, belegId, belegDto, true).pipe(
              switchMap(updatedBelegDto => {
                this.logger.debug(
                  'update fakturierungsbeleg entity succeeded. belegId:',
                  updatedBelegDto.id,
                );
                return [
                  FakturierungsbelegEntitiesActions.updateFakturierungsbelegSuccess({belegDto: updatedBelegDto}),
                  // INFO: Positionen neu laden da Besteuerungsform  die USt-Saetze ändert
                  FakturierungsbelegFormActions.fetchPositionen({belegId}),
                ];
              }),
              catchError(error => of(error).pipe(
                mappedHttpErrorResponseOperator(error),
                map(error => {
                  this.logger.error(
                    'update fakturierungsbeleg entity failed. belegId:',
                    belegId,
                    'error:',
                    error,
                  );

                  return FakturierungsbelegEntitiesActions.updateFakturierungsbelegEinstellungenFailure({
                    error,
                  });
                }),
              )),
            );
          })
        );
      }),
    )
  );

  readonly updateFakturierungsbelegEinstellungenFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.updateFakturierungsbelegEinstellungenFailure),
      map(action => {
        let errorMsg = '';
        switch (action.error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Aktualisieren der Belegeinstellungen. ' +
              'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404 : {
            errorMsg = 'Betrieb nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Aktualisieren der Belegeinstellungen. Bitte probiere es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

}
